import { useMutation, useQueryClient } from '@tanstack/react-query';

import { replaceWorkspaceSecretValue } from '@/lib/api/generated/api';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';

import { toSecretError } from '../errors';
import { secretKeys } from '../queryKeys';

/**
 * Replace one entry's value.
 *
 * The caller supplies only the new value — it does not know the current one and
 * does not need to, which is what makes rotating a secret possible at all. The
 * key and the kind are not sent: the service refuses a body carrying either,
 * because neither can change (FR-014, FR-015).
 */
export function useReplaceWorkspaceSecretValue(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      key,
      value,
    }: {
      key: string;
      value: string;
    }): Promise<WorkspaceSecretView> => {
      const response = await replaceWorkspaceSecretValue(workspaceId, key, {
        value,
      });
      if (response.status === 200) return response.data;
      throw toSecretError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
